import { abortReason, throwIfAborted } from '@pisano/feedback-core/app/utils/abortSignal';
import { RequestError } from '@pisano/feedback-core/app/utils/request';
import {
  CAPTCHA_PROVIDER_ALTCHA,
  CAPTCHA_PROVIDER_RECAPTCHA,
} from '@pisano/feedback-core/app/containers/Flow/utils/captcha';
import {
  FEEDBACK_SEND_RECAPTCHA_ACTION,
  getRecaptchaToken,
  isRecaptchaRuntimeFailure,
} from '@pisano/feedback-core/app/containers/Flow/utils/recaptcha';

const RECAPTCHA_TIMEOUT_MS = 30_000;
const ALTCHA_TIMEOUT_MS = 90_000;

async function prepareWithDeadline(prepare, signal, timeout) {
  throwIfAborted(signal);

  const controller = new AbortController();
  const abort = () => controller.abort(abortReason(signal));
  let onAbort;
  const interrupted = new Promise((resolve, reject) => {
    onAbort = () => reject(abortReason(controller.signal));
    controller.signal.addEventListener('abort', onAbort, { once: true });
  });
  signal?.addEventListener('abort', abort, { once: true });
  const timer = setTimeout(
    () => controller.abort(new RequestError('timeout', 'Captcha preparation timed out')),
    timeout,
  );

  try {
    return await Promise.race([prepare(controller), interrupted]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
    controller.signal.removeEventListener('abort', onAbort);
  }
}

/** Each queued attempt owns its token and workers; only the script loader is shared. */
export function createQueueCaptchaProvider({ apiUrl }) {
  return async (requirements, { signal } = {}) => {
    throwIfAborted(signal);

    if (!requirements.required) return undefined;

    if (globalThis.navigator?.onLine === false) throw new RequestError('network', 'Captcha preparation is offline');

    const provider = requirements.provider ?? CAPTCHA_PROVIDER_RECAPTCHA;

    if (![CAPTCHA_PROVIDER_RECAPTCHA, CAPTCHA_PROVIDER_ALTCHA].includes(provider)) {
      throw new RequestError('captcha', 'Unsupported captcha provider');
    }

    if (provider === CAPTCHA_PROVIDER_RECAPTCHA) {
      try {
        const token = await prepareWithDeadline(
          (controller) =>
            getRecaptchaToken(FEEDBACK_SEND_RECAPTCHA_ACTION, { required: true, signal: controller.signal }),
          signal,
          RECAPTCHA_TIMEOUT_MS,
        );

        return { provider, token };
      } catch (error) {
        throwIfAborted(signal);

        if (!isRecaptchaRuntimeFailure(error) && error?.kind !== 'timeout') throw error;

        // A disconnect is transient even if connectivity returns before the
        // failure is persisted; an unclassified error would block the record.
        if (globalThis.navigator?.onLine === false) {
          throw new RequestError('network', 'Captcha preparation is offline');
        }
      }
    }

    return prepareWithDeadline(
      async (controller) => {
        const { getQueueAltchaToken } = await import('./feedbackQueueAltcha');
        throwIfAborted(controller.signal);
        const token = await getQueueAltchaToken({ apiUrl, controller });

        return { provider: CAPTCHA_PROVIDER_ALTCHA, token };
      },
      signal,
      ALTCHA_TIMEOUT_MS,
    );
  };
}
